import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'

export default function Reports() {
  const [groups, setGroups] = useState([])
  const [sel, setSel] = useState(null)
  const [report, setReport] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { fetchGroups() }, [])
  useEffect(() => { if (sel) buildReport() }, [sel])

  async function fetchGroups() {
    const { data } = await supabase.from('groups').select('*').order('grade_level').order('name')
    setGroups(data||[])
    if (data?.length) setSel(data[0])
    setLoading(false)
  }

  async function buildReport() {
    setLoading(true)
    const { data: students } = await supabase.from('students').select('*').eq('group_id', sel.id).order('name')
    if (!students?.length) { setReport([]); setLoading(false); return }
    const ids = students.map(s=>s.id)
    const [{ data: gr }, { data: at }, { data: hw }] = await Promise.all([
      supabase.from('grades').select('*').in('student_id', ids),
      supabase.from('attendance').select('*').in('student_id', ids),
      supabase.from('homework').select('*').in('student_id', ids),
    ])
    const result = students.map(s => {
      const sg = gr?.filter(g=>g.student_id===s.id)||[]
      const sa = at?.filter(a=>a.student_id===s.id)||[]
      const sh = hw?.filter(h=>h.student_id===s.id)||[]
      const avg   = sg.length ? Math.round(sg.reduce((a,g)=>a+(g.score/g.total*100),0)/sg.length) : null
      const att   = sa.length ? Math.round(sa.filter(a=>a.status==='present').length/sa.length*100) : null
      const hwPct = sh.length ? Math.round(sh.filter(h=>h.status==='done').length/sh.length*100) : null
      const ov    = Math.round(((avg??50)*.5)+((att??50)*.3)+((hwPct??50)*.2))
      const lbl   = ov>=90?'Excellent':ov>=75?'Very Good':ov>=60?'Good':ov>=50?'Pass':'Fail'
      const col   = ov>=90?'var(--c-green)':ov>=75?'var(--c-accent)':ov>=60?'var(--c-purple)':ov>=50?'var(--c-amber)':'var(--c-red)'
      const bCls  = ov>=90?'badge-green':ov>=75?'badge-accent':ov>=60?'badge-purple':ov>=50?'badge-amber':'badge-red'
      return { ...s, avg, att, hwPct, ov, lbl, col, bCls }
    })
    result.sort((a,b)=>b.ov-a.ov)
    setReport(result); setLoading(false)
  }

  function pBadge(p) {
    if (p==null) return <span style={{color:'var(--c-text3)'}}>—</span>
    const cls = p>=80?'badge-green':p>=60?'badge-amber':'badge-red'
    return <span className={`badge ${cls}`}>{p}%</span>
  }

  if (loading) return <div className="empty"><div className="empty-icon">⏳</div><div className="empty-title">Loading...</div></div>

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Reports</h2>
      </div>

      <div className="chips" style={{marginBottom:16}}>
        {groups.map(g=>(
          <button key={g.id} className={`chip ${sel?.id===g.id?'active':''}`} onClick={()=>setSel(g)}>
            {g.name}
          </button>
        ))}
      </div>

      {report.length===0 ? (
        <div className="empty">
          <div className="empty-icon">📊</div>
          <div className="empty-title">No data yet</div>
          <div className="empty-sub">Add students and record their data first</div>
        </div>
      ) : (<>

        {/* Desktop */}
        <div className="desktop-only">
          <div className="list-card">
            <table>
              <thead><tr>
                <th>#</th><th>Student</th><th>Exams</th><th>Attendance</th><th>Homework</th><th>Overall</th>
              </tr></thead>
              <tbody>
                {report.map((s,i)=>(
                  <tr key={s.id}>
                    <td style={{color:'var(--c-text3)',fontWeight:700}}>{i+1}</td>
                    <td>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <div className="avatar avatar-sm">{s.name.charAt(0).toUpperCase()}</div>
                        <div>
                          <div style={{fontWeight:600}}>{s.name}</div>
                          {s.grade&&<div style={{fontSize:12,color:'var(--c-text3)'}}>{s.grade}</div>}
                        </div>
                      </div>
                    </td>
                    <td>{pBadge(s.avg)}</td>
                    <td>{pBadge(s.att)}</td>
                    <td>{pBadge(s.hwPct)}</td>
                    <td><span className={`badge ${s.bCls}`}>{s.lbl} · {s.ov}%</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile */}
        <div className="mobile-only" style={{display:'flex',flexDirection:'column',gap:10}}>
          {report.map((s,i)=>(
            <div key={s.id} className="card" style={{padding:'14px 16px'}}>
              <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:12}}>
                <div style={{
                  width:36,height:36,borderRadius:'50%',
                  background:'var(--c-accent-l)',color:'var(--c-accent)',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontWeight:700,fontSize:15,flexShrink:0
                }}>
                  {i+1}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontWeight:700,fontSize:15,color:'var(--c-text)'}}>{s.name}</div>
                  {s.grade&&<div style={{fontSize:12,color:'var(--c-text3)',marginTop:1}}>{s.grade}</div>}
                </div>
                <span className={`badge ${s.bCls}`}>{s.lbl}</span>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
                {[
                  {l:'Exams',    v:s.avg},
                  {l:'Attend.',  v:s.att},
                  {l:'Homework', v:s.hwPct},
                ].map(({l,v})=>(
                  <div key={l} style={{background:'var(--c-surface2)',borderRadius:8,padding:'8px 10px',textAlign:'center'}}>
                    <div style={{fontSize:10,color:'var(--c-text3)',fontWeight:600,textTransform:'uppercase',marginBottom:4}}>{l}</div>
                    {pBadge(v)}
                  </div>
                ))}
              </div>
              <div style={{marginTop:10,paddingTop:10,borderTop:'1px solid var(--c-border)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                <span style={{fontSize:12,color:'var(--c-text3)'}}>Overall Score</span>
                <span style={{fontSize:16,fontWeight:800,color:s.col}}>{s.ov}%</span>
              </div>
            </div>
          ))}
        </div>
      </>)}
    </div>
  )
}